import type { Home } from '@lmc/ts-common'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import Operations from 'operations'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

type HomeFeature = { granted?: boolean; enabled?: boolean }

type HomeFeatures = Record<string, HomeFeature>

export interface UseToggleHomeFeatureReturn {
  /**
   * Flips a single `home.features[featureKey].enabled` flag and persists it via
   * `saveHomeDetails` (partial home update). Keeps every other feature
   * untouched. `featureLabel` (e.g. "Device approval") is used in the toast.
   */
  toggleFeature: (
    featureKey: string,
    enabled: boolean,
    featureLabel?: string
  ) => Promise<void>
  isSaving: boolean
}

/**
 * Shared hook for toggling a single home feature flag on/off from the page
 * where the feature lives (Device Access Control, Shift Passwords, ...).
 * Reads `state.global.home`, merges one `features[key] = { granted, enabled }`,
 * dispatches `saveHomeDetails` with a minimal `{ id, features }` payload,
 * tracks `isSaving`, and shows a success/error toast.
 */
export const useToggleHomeFeature = (): UseToggleHomeFeatureReturn => {
  const dispatch = useDispatch()

  const { t } = useTranslation(NAMESPACE.GENERAL)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const [isSaving, setIsSaving] = useState(false)

  const toggleFeature = async (
    featureKey: string,
    enabled: boolean,
    featureLabel?: string
  ): Promise<void> => {
    setIsSaving(true)

    const features = (home?.features ?? {}) as HomeFeatures

    try {
      // Send only `id` (for the PUT URL) + `features`. The home-update endpoint
      // does a partial merge, and its Zod schema validates any field that is
      // present — so spreading the whole redux `home` (which holds populated
      // refs like `homeGroup`/`privacyOfficers` and nullable fields) fails
      // validation. The old settings form worked precisely because it sent a
      // curated subset; a feature toggle only needs `features`.
      const saved = await dispatch(
        Operations.saveHomeDetails({
          id: home?.id,
          features: {
            ...features,
            [featureKey]: { ...features[featureKey], enabled }
          }
        })
      )
      // saveHomeDetails swallows non-safeguarding failures, so it resolves even
      // when the PUT fails; a falsy result means it didn't persist — throw so
      // the catch below shows the error toast instead of a false success.
      if (!saved) {
        throw new Error('saveHomeDetails did not persist the feature toggle')
      }

      showToast({
        type: 'success',
        title: t(
          enabled
            ? generalConstants.FEATURE_TURNED_ON
            : generalConstants.FEATURE_TURNED_OFF,
          { feature: featureLabel }
        )
      })
    } catch {
      showToast({
        type: 'error',
        title: t(generalConstants.COULDNT_UPDATE_FEATURE, {
          feature: featureLabel
        })
      })
    } finally {
      setIsSaving(false)
    }
  }

  return { toggleFeature, isSaving }
}

export default useToggleHomeFeature
